import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useCarriers, useTrunks } from '../../lib/api/hooks-billing.js';
import { useTenants } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { formatMoney } from '../../lib/format.js';

/**
 * DID inventory: the numbers the platform owns.
 *
 * MSP-side, because a number is stock -- bought from a carrier, billed monthly,
 * assigned to a customer. What the customer does with it once assigned (where
 * it routes, the business-hours split) is theirs and lives in their own portal.
 *
 * This screen exists because there was no way to add a number at all, which is
 * what made inbound routing unreachable: the routing screen had nothing to
 * route.
 */

interface NumberRow {
  id: string;
  e164: string;
  tenant_id: string | null;
  tenant_name: string | null;
  carrier_name: string | null;
  number_type: string;
  status: string;
  monthly_cost: string | null;
  monthly_price: string | null;
  currency: string;
  has_route: boolean;
}

export function NumberInventoryPage() {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const [filter, setFilter] = useState<'all' | 'unassigned' | 'assigned'>('all');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  const key = ['numbers', filter, search] as const;
  const numbers = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ data: NumberRow[] }>(
      `/v1/numbers?${new URLSearchParams({
        ...(filter === 'unassigned' ? { unassigned: 'true' } : {}),
        ...(filter === 'assigned' ? { status: 'assigned' } : {}),
        ...(search ? { search } : {}),
      })}`),
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['numbers'] });

  const assign = useMutation({
    mutationFn: ({ id, tenantId }: { id: string; tenantId: string | null }) =>
      api.patch(`/v1/numbers/${id}`, { tenant_id: tenantId }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/numbers/${id}`),
    onSuccess: invalidate,
  });

  if (numbers.error) return <ErrorBanner error={numbers.error} />;

  const rows = numbers.data?.data ?? [];
  const free = rows.filter((n) => !n.tenant_id).length;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">DID numbers</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            {rows.length} number{rows.length === 1 ? '' : 's'}
            {free > 0 && <> · {free} unassigned</>}
          </p>
        </div>
        {can('number:order') && !adding && (
          <button type="button" className="arc-btn arc-btn-primary" onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> Add numbers
          </button>
        )}
      </div>

      {adding && (
        <AddNumbers onCancel={() => setAdding(false)}
          onDone={() => { setAdding(false); invalidate(); }} />
      )}

      <div className="arc-toolbar" style={{ marginBottom: 12 }}>
        <select className="arc-select" style={{ width: 'auto' }} value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          aria-label="Filter numbers">
          <option value="all">All numbers</option>
          <option value="unassigned">Unassigned only</option>
          <option value="assigned">Assigned only</option>
        </select>
        <input className="arc-input arc-mono" style={{ maxWidth: 200 }}
          placeholder="Search digits" value={search}
          onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="arc-card">
        {numbers.data === undefined ? (
          <TableSkeleton rows={6} cols={5} />
        ) : rows.length === 0 ? (
          <Empty icon="hash" title="No numbers"
            hint="DIDs arrive from a carrier in blocks — paste the whole list at once rather than adding them one at a time." />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Number</th><th>Type</th><th>Carrier</th>
                  <th>Assigned to</th><th>Monthly</th>
                  {can('number:assign') && <th style={{ width: 210 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((n) => (
                  <tr key={n.id}>
                    <td className="arc-mono" style={{ fontWeight: 600 }}>{n.e164}</td>
                    <td className="arc-subtle">{n.number_type.replace(/_/g, ' ')}</td>
                    <td className="arc-subtle">{n.carrier_name ?? '—'}</td>
                    <td>
                      {n.tenant_name ? (
                        <div className="arc-row" style={{ gap: 6 }}>
                          <span>{n.tenant_name}</span>
                          {/*
                            An assigned number with no route rings nowhere. The
                            customer sees it as a dead line and the cause is on
                            this screen, so it is flagged here too.
                          */}
                          {!n.has_route && (
                            <span className="arc-badge arc-badge-warn">
                              <Icon name="warning" size={10} /> no route
                            </span>
                          )}
                        </div>
                      ) : (
                        <StatusBadge status={n.status} />
                      )}
                    </td>
                    <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {n.monthly_price
                        ? formatMoney(n.monthly_price, n.currency)
                        : <span className="arc-subtle">—</span>}
                    </td>
                    {can('number:assign') && (
                      <td>
                        <AssignCell number={n}
                          onAssign={(tenantId) => assign.mutate({ id: n.id, tenantId })}
                          onDelete={() => remove.mutate(n.id)}
                          pending={assign.isPending || remove.isPending}
                          canDelete={can('number:release')} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function AssignCell({ number, onAssign, onDelete, pending, canDelete }: {
  number: NumberRow;
  onAssign: (tenantId: string | null) => void;
  onDelete: () => void;
  pending: boolean;
  canDelete: boolean;
}) {
  const tenants = useTenants();
  const [picking, setPicking] = useState(false);

  if (number.tenant_id) {
    return (
      <div className="arc-row" style={{ gap: 4 }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
          disabled={pending}
          title="Take the number back. Its routing is removed with it."
          onClick={() => onAssign(null)}>
          Release
        </button>
      </div>
    );
  }

  if (!picking) {
    return (
      <div className="arc-row" style={{ gap: 4 }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
          onClick={() => setPicking(true)}>Assign</button>
        {canDelete && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
            title="Remove from inventory" disabled={pending} onClick={onDelete}>
            <Icon name="trash" size={12} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="arc-row" style={{ gap: 4 }}>
      <select className="arc-select" style={{ width: 'auto' }} defaultValue=""
        disabled={pending}
        onChange={(e) => { if (e.target.value) onAssign(e.target.value); }}>
        <option value="">Choose customer…</option>
        {(tenants.data?.data ?? []).map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
        onClick={() => setPicking(false)}>Cancel</button>
    </div>
  );
}

/**
 * Numbers are added in BLOCKS.
 *
 * A carrier hands over a range, not a number, and a form that takes one at a
 * time is a form nobody keeps the inventory current with. Pasting is the whole
 * interaction: anything that looks like a number on its own line is taken.
 */
function AddNumbers({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const [text, setText] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [trunkId, setTrunkId] = useState('');
  const [numberType, setNumberType] = useState('geographic');
  const [price, setPrice] = useState('');
  const carriers = useCarriers();
  const trunks = useTrunks(carrierId || undefined);

  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ added: number; skipped: string[] }>('/v1/numbers', body),
    onSuccess: () => onDone(),
  });

  /**
   * Anything digit-shaped, one per line or comma separated, with a `+` added
   * when it is missing. Operators paste from spreadsheets and emails, and
   * refusing a list because one entry lacks a plus is a refusal they will work
   * around by hand.
   */
  const parsed = [...new Set(
    text.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean)
      .map((x) => (x.startsWith('+') ? x : `+${x.replace(/\D/g, '')}`))
      .filter((x) => /^\+[1-9]\d{6,14}$/.test(x)),
  )];
  const rejected = text.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean).length - parsed.length;

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <div className="arc-card-head"><h2 className="arc-h2">Add numbers</h2></div>
      <div className="arc-card-body">
        <label className="arc-field">
          <span>Numbers</span>
          <textarea className="arc-input arc-mono" rows={5} value={text}
            placeholder={'+27821234567\n+27821234568\n…'}
            onChange={(e) => setText(e.target.value)}
            style={{ resize: 'vertical', width: '100%' }} />
          <div className="arc-hint">
            {parsed.length > 0
              ? `${parsed.length} valid number${parsed.length === 1 ? '' : 's'}`
              : 'One per line, or comma separated.'}
            {rejected > 0 && (
              <span className="arc-badge arc-badge-warn" style={{ marginLeft: 8 }}>
                {rejected} not recognised
              </span>
            )}
          </div>
        </label>

        <div className="arc-grid arc-grid-4" style={{ gap: 10, marginTop: 10 }}>
          <label className="arc-field">
            <span>Carrier</span>
            <select className="arc-select" value={carrierId}
              onChange={(e) => { setCarrierId(e.target.value); setTrunkId(''); }}>
              <option value="">Unknown</option>
              {(carriers.data?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="arc-field">
            <span>Delivered on trunk</span>
            <select className="arc-select" value={trunkId}
              onChange={(e) => setTrunkId(e.target.value)}>
              <option value="">Any</option>
              {(trunks.data?.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <label className="arc-field">
            <span>Type</span>
            <select className="arc-select" value={numberType}
              onChange={(e) => setNumberType(e.target.value)}>
              {['geographic', 'non_geographic', 'tollfree', 'shared_cost',
                'mobile', 'international'].map((k) => (
                <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </label>
          <label className="arc-field">
            <span>Monthly price</span>
            <input className="arc-input arc-num" value={price} inputMode="decimal"
              placeholder="45.00" onChange={(e) => setPrice(e.target.value)} />
          </label>
        </div>

        {add.data && add.data.skipped.length > 0 && (
          <div className="arc-banner arc-banner-info" style={{ marginTop: 10 }}>
            <Icon name="info" size={14} />
            <span>
              {add.data.added} added; {add.data.skipped.length} already on the
              platform and left alone.
            </span>
          </div>
        )}

        <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="arc-btn arc-btn-primary"
            disabled={parsed.length === 0 || add.isPending}
            onClick={() => add.mutate({
              e164: parsed,
              ...(carrierId ? { carrier_id: carrierId } : {}),
              ...(trunkId ? { trunk_id: trunkId } : {}),
              number_type: numberType,
              ...(price.trim() ? { monthly_price: price.trim() } : {}),
            })}>
            {add.isPending ? 'Adding…' : `Add ${parsed.length || ''} number${parsed.length === 1 ? '' : 's'}`.replace('  ', ' ')}
          </button>
        </div>
      </div>
    </div>
  );
}
